<script lang="ts">
export const elModalSizes = ['xs', 'm', 'l', 'xl'] as const;
export type ElModalSize = (typeof elModalSizes)[number];

export const elModalSizesBreakpoints = { xs: 360, m: 576, l: 768, xl: 1280 };

export const elModalColors = ['primary', 'secondary', 'error'] as const;
export type ElModalColor = (typeof elModalColors)[number];
</script>

<script setup lang="ts">
import { onKeyStroke, useElementSize, useScroll, useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { computed, ref, watch, onUnmounted, nextTick } from 'vue';

import ElButton from '@/ElButton.vue';
import ElIcon from '@/ElIcon.vue';
import ElIconButton from '@/ElIconButton.vue';
import CustomTransition from '@/_CustomTransition.vue';
import { useModalStack } from '@/composable/useModalStack';
import { useMobile } from '@/composable/mobileComposable';

export interface ElModalProps {
  size?: ElModalSize;
  color?: ElModalColor;
  headerIcon: InstanceType<typeof ElIcon>['$props'];
  title: string;
  description?: string;
  /** Opt-in: on mobile touch devices, hide header and/or actions while an input/textarea in the body is focused. */
  hideOnFocus?: { header?: boolean; actions?: boolean };
  /** Opt-in: on touch devices, align modal to top (items-start mt-2) instead of center. Avoids keyboard shifting. */
  alignTopOnTouchDevice?: boolean;
  primaryAction: Omit<InstanceType<typeof ElButton>['$props'], 'size' | 'variant'>;
  secondaryAction?: Omit<InstanceType<typeof ElButton>['$props'], 'size' | 'variant'>;
  tertiaryAction?: Omit<InstanceType<typeof ElButton>['$props'], 'size' | 'variant'>;
}

const isOpen = defineModel('isOpen', {
  default: false,
  required: true,
  type: Boolean,
});

const emit = defineEmits<{ (event: 'closed'): void }>();

const props = withDefaults(defineProps<ElModalProps>(), {
  size: 'm',
  color: 'primary',
  title: undefined,
  description: undefined,
  hideOnFocus: undefined,
  alignTopOnTouchDevice: false,
  secondaryAction: undefined,
  tertiaryAction: undefined,
});

const closeModal = () => (isOpen.value = false);

const backdrop = ref<HTMLElement | null>(null);
const modalBody = ref<HTMLElement | null>(null);
const isInputFocused = ref(false);

const { isMobile, isTouchDevice } = useMobile();

const mobileInputFocused = computed(() => isMobile.value && isTouchDevice.value && isInputFocused.value);
const shouldHideHeader = computed(() => props.hideOnFocus?.header && mobileInputFocused.value);
const shouldHideActions = computed(() => props.hideOnFocus?.actions && mobileInputFocused.value);

const isInputOrTextarea = (el: EventTarget | null): el is HTMLInputElement | HTMLTextAreaElement => {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
};

const onBodyFocusIn = (e: FocusEvent) => {
  const target = e.target;

  if (isInputOrTextarea(target)) {
    isInputFocused.value = true;
    if (isMobile.value && isTouchDevice.value) {
      setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' }), 250);
    }
  }
};

const onBodyFocusOut = () => {
  nextTick(() => {
    const active = document.activeElement;
    if (!modalBody.value || !modalBody.value.contains(active) || !isInputOrTextarea(active)) {
      isInputFocused.value = false;
    }
  });
};

const isInErrorState = computed(() => props.color === 'error');

const { arrivedState } = useScroll(modalBody);
const { height } = useElementSize(modalBody);
const { width: windowWidth } = useWindowSize();

const isModalScrollable = computed(() => modalBody.value && modalBody.value.scrollHeight > height.value);

const { registerModal, unregisterModal } = useModalStack();

const responsiveSize = computed(() => {
  for (let i = elModalSizes.indexOf(props.size); i >= 0; i--) {
    if (windowWidth.value >= elModalSizesBreakpoints[elModalSizes[i]]) return elModalSizes[i];
  }

  return 'full'; // have the modal take the full width of the screen if no size is found
});

const onClickOutside = (e: TouchEvent | MouseEvent) => {
  // This prevents the modal from closing when clicking on elements that use ElClipToAnchor (eg. ElInputSelect)
  const isClickOnBackdrop = e.target === backdrop.value;

  if (isClickOnBackdrop) closeModal();
};

watch(
  () => isOpen.value,
  value => {
    if (value) {
      registerModal();
    } else {
      isInputFocused.value = false;
      unregisterModal();
      emit('closed');
    }
  },
);

onUnmounted(() => {
  if (isOpen.value) {
    unregisterModal();
  }
});

onKeyStroke('Escape', e => {
  e.preventDefault();
  closeModal();
});
</script>

<template>
  <Teleport to="body">
    <CustomTransition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-1 inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-6 sm:p-0"
        :class="props.alignTopOnTouchDevice && isTouchDevice ? 'items-start mt-2' : 'items-center'"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 transition-opacity">
          <div ref="backdrop" class="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <!-- Modal -->
        <div
          v-on-click-outside="onClickOutside"
          class="flex flex-col gap-7 py-8 z-50 bg-neutral-surface shadow-md rounded-md border border-neutral-surface max-h-[90%] md:max-h-[80%] text-neutral-darker"
          :class="{
            'w-[360px]': responsiveSize === 'xs',
            'w-[576px]': responsiveSize === 'm',
            'w-[768px]': responsiveSize === 'l',
            'w-[1280px]': responsiveSize === 'xl',
            'w-full': responsiveSize === 'full',
          }"
          :data-cy="`el-modal-${title?.replaceAll(' ', '')}`"
        >
          <!-- Header -->
          <div
            v-if="!shouldHideHeader"
            class="flex items-center justify-between gap-2"
            :class="{ 'px-4': responsiveSize === 'xs', 'px-8': responsiveSize !== 'xs' }"
          >
            <div class="flex items-center justify-between gap-2 flex-1 overflow-hidden">
              <div
                :class="{
                  'text-error bg-error-light-active': isInErrorState,
                  'text-primary bg-neutral-surface-raised': color === 'primary',
                  'text-secondary bg-neutral-surface-raised': color === 'secondary',
                }"
                class="!h-8 !w-8 rounded-full"
              >
                <ElIcon v-bind="isInErrorState ? { name: 'ExclamationCircleIcon' } : headerIcon" class="p-1" />
              </div>

              <div class="text-xl font-semibold truncate flex-1">{{ title }}</div>
            </div>

            <ElIconButton :icon="{ name: 'XMarkIcon' }" :error="isInErrorState" @click="closeModal" />
          </div>

          <!-- Body -->
          <div
            ref="modalBody"
            class="flex flex-col gap-7 border-neutral-surface overflow-y-auto overflow-x-hidden"
            :class="{
              'px-4': responsiveSize === 'xs',
              'px-8': responsiveSize !== 'xs',
              'border-t': isModalScrollable && !arrivedState.top,
              'border-b': isModalScrollable && !arrivedState.bottom,
            }"
            @focusin="onBodyFocusIn"
            @focusout="onBodyFocusOut"
          >
            <p v-if="description" class="text-base font-normal text-wrap break-words">{{ description }}</p>

            <slot name="default"></slot>
          </div>

          <!-- Footer -->
          <div
            v-if="!shouldHideActions && (primaryAction || secondaryAction || tertiaryAction)"
            class="flex items-center gap-4"
            :class="{ 'flex-col-reverse px-4': responsiveSize === 'xs', 'flex-row px-8': responsiveSize !== 'xs' }"
          >
            <ElButton
              v-if="tertiaryAction"
              v-bind="tertiaryAction"
              variant="tertiary"
              :class="{ 'w-full': responsiveSize === 'xs' }"
              :size="responsiveSize === 'xs' ? 'l' : 'base'"
              :error="isInErrorState || tertiaryAction.error"
            />

            <div
              class="flex gap-4"
              :class="{ 'flex-col-reverse w-full': responsiveSize === 'xs', 'flex-row ml-auto': responsiveSize !== 'xs' }"
            >
              <ElButton
                v-if="secondaryAction"
                v-bind="secondaryAction"
                variant="secondary"
                :size="responsiveSize === 'xs' ? 'l' : 'base'"
                :error="isInErrorState || secondaryAction.error"
              />

              <ElButton
                v-if="primaryAction"
                v-bind="primaryAction"
                variant="primary"
                :size="responsiveSize === 'xs' ? 'l' : 'base'"
                :error="isInErrorState || primaryAction.error"
              />
            </div>
          </div>
        </div>
      </div>
    </CustomTransition>
  </Teleport>
</template>
